import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { DeliveryType, PriceChange } from '@alkor/types';
import { api, ApiClientError, type OrderConflict } from '@/lib/api';
import { useShop } from '@/state';
import { track } from '@/lib/analytics';
import { formatPhone, formatPrice, isValidPhone, pluralize } from '@alkor/utils';
import { haptic, requestPhone, setClosingConfirmation } from '@/lib/telegram';
import { IconInfo, IconTelegram } from '@/components/icons';
import { Notice, Segmented } from '@/components/ui';
import { MainButton } from '@/components/MainButton';

const PICKUP_ADDRESS = (import.meta.env.VITE_PICKUP_ADDRESS as string | undefined)
  ?? 'Москва, ул. Кусковская, 20А. Ежедневно 11:00–19:00, по договорённости.';

const COMPANIES = ['СДЭК', 'ПЭК', 'Деловые линии'];

export function CheckoutScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { cart, showToast } = useShop();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [delivery, setDelivery] = useState<DeliveryType>('pickup');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [company, setCompany] = useState(COMPANIES[0]!);
  const [comment, setComment] = useState('');
  const [touchedPhone, setTouchedPhone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState<PriceChange[] | null>(null);

  useEffect(() => { track('checkout_start'); }, []);

  // Предупреждение при закрытии — только когда форма уже начата
  const dirty = name.length > 0 || phone.length > 0 || address.length > 0 || comment.length > 0;
  useEffect(() => {
    setClosingConfirmation(dirty);
    return () => setClosingConfirmation(false);
  }, [dirty]);

  // После успешного оформления корзина обнуляется — защитный редирект
  // на /cart не должен перебить переход на экран «Заказ принят».
  const submitted = useRef(false);
  useEffect(() => {
    if (submitted.current) return;
    if (!cart || cart.items.length === 0) navigate('/cart', { replace: true });
  }, [cart, navigate]);

  if (!cart || cart.items.length === 0) return null;

  const phoneValid = isValidPhone(phone);
  const phoneError = touchedPhone && phone.length > 0 && !phoneValid;
  const requiredOk =
    name.trim().length >= 2 &&
    phoneValid &&
    (delivery !== 'courier' || address.trim().length > 0) &&
    (delivery !== 'shipping' || city.trim().length > 0);

  const submit = async () => {
    if (!requiredOk || submitting) return;
    setSubmitting(true);
    setConflict(null);
    try {
      const order = await api.createOrder({
        items: cart.items.map((i) => ({ productId: i.product.id, quantity: i.quantity })),
        clientTotal: cart.itemsTotal,
        customer: { name: name.trim(), phone },
        delivery: {
          type: delivery,
          ...(delivery === 'shipping' ? { city: city.trim(), company } : {}),
          ...(delivery === 'courier' ? { address: address.trim() } : {}),
        },
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      });
      submitted.current = true;
      haptic.success();
      navigate(`/order/${order.orderNumber}`, { replace: true, state: { order } });
      // Инвалидация после навигации: экран успеха уже смонтирован
      void qc.invalidateQueries({ queryKey: ['cart'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    } catch (e) {
      haptic.error();
      if (e instanceof ApiClientError && e.code === 'PRICE_CHANGED') {
        // Молча пересчитать корзину нельзя: человек должен увидеть, что изменилось
        const details = e.details as OrderConflict | undefined;
        setConflict(details?.changes ?? []);
        await qc.invalidateQueries({ queryKey: ['cart'] });
      } else if (e instanceof ApiClientError) {
        showToast(e.message);
        if (e.code === 'CART_ITEM_UNAVAILABLE' || e.code === 'CART_EMPTY') {
          await qc.invalidateQueries({ queryKey: ['cart'] });
          navigate('/cart');
        }
      } else {
        showToast('Не удалось оформить заказ');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="screen-title">Оформление</div>
      <div className="screen-sub">
        {pluralize(cart.count, 'позиция', 'позиции', 'позиций')} на {formatPrice(cart.itemsTotal)}
      </div>

      {conflict && (
        <div style={{ marginTop: 14 }}>
          <Notice variant="warn">
            Сумма изменилась, пока вы оформляли заказ.
            {conflict.length > 0 && (
              <>
                {' '}
                {conflict.map((c) => `${c.productName}: ${formatPrice(c.oldPrice)} → ${formatPrice(c.newPrice)}`).join('; ')}.
              </>
            )}
            {' '}Проверьте корзину и подтвердите заново.
          </Notice>
        </div>
      )}

      <div className="field">
        <label htmlFor="name">Имя</label>
        <input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Как к вам обращаться"
          autoComplete="name"
        />
      </div>

      <div className={`field${phoneError ? ' field--err' : ''}`}>
        <label htmlFor="phone">Телефон</label>
        <input
          id="phone"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
          onBlur={() => setTouchedPhone(true)}
          placeholder="+7 (___) ___-__-__"
          inputMode="tel"
          autoComplete="tel"
        />
        {phoneError && (
          <div className="field__err"><IconInfo size={14} />Нужно 11 цифр — проверьте номер</div>
        )}
        <button
          type="button"
          className="tgbtn"
          onClick={async () => {
            const p = await requestPhone();
            if (p) {
              setPhone(formatPhone(p));
              haptic.light();
            } else {
              showToast('Ваш Telegram не поддерживает передачу номера — введите вручную');
            }
          }}
        >
          <IconTelegram size={16} />
          Взять номер из Telegram
        </button>
      </div>

      <div className="field">
        <label>Способ доставки</label>
        <Segmented<DeliveryType>
          value={delivery}
          onChange={setDelivery}
          options={[
            { value: 'pickup', label: 'Самовывоз' },
            { value: 'courier', label: <>Курьер<br />по городу</> },
            { value: 'shipping', label: <>ТК<br />по России</> },
          ]}
        />
      </div>

      {/* Показываем только поля, относящиеся к выбранному способу */}
      {delivery === 'pickup' && (
        <div style={{ marginTop: 14 }}><Notice>{PICKUP_ADDRESS}</Notice></div>
      )}

      {delivery === 'courier' && (
        <div className="field">
          <label htmlFor="address">Адрес доставки</label>
          <input
            id="address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Улица, дом, квартира"
            autoComplete="street-address"
          />
          <div className="field__hint">В день заказа при оформлении до 15:00</div>
        </div>
      )}

      {delivery === 'shipping' && (
        <>
          <div className="field">
            <label htmlFor="city">Город</label>
            <input
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Куда отправить"
              autoComplete="address-level2"
            />
          </div>
          <div className="field">
            <label>Транспортная компания</label>
            <Segmented
              value={company}
              onChange={setCompany}
              options={COMPANIES.map((c) => ({ value: c, label: c }))}
            />
          </div>
        </>
      )}

      <div className="field">
        <label htmlFor="comment">Комментарий</label>
        <textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Пожелания к заказу, удобное время звонка"
        />
      </div>

      <div style={{ marginTop: 18 }}>
        <Notice>
          Итоговую сумму пересчитает сервер по актуальным ценам. Если что-то изменилось — покажем до подтверждения.
        </Notice>
      </div>

      <div className="pad" />
      <div className="pad" />

      <MainButton
        text={requiredOk ? 'Подтвердить заказ' : 'Заполните имя и телефон'}
        suffix={requiredOk ? formatPrice(cart.itemsTotal) : undefined}
        disabled={!requiredOk}
        loading={submitting}
        onClick={() => void submit()}
      />
    </>
  );
}
